/**
 * Reconnecting WebSocket client for the HedgePair DO's /ws endpoint.
 *
 * Usage:
 *   const ws = createHedgeSocket("P1", (ev) => { ... });
 *   ws.close();
 */

import type { ServerEvent } from "@pfh/shared";

export interface HedgeSocket {
  close(): void;
  send(msg: unknown): boolean;
}

export function createHedgeSocket(
  pairId: string,
  onEvent: (ev: ServerEvent) => void,
  opts: {
    backoffMs?: number;
    onOpen?: () => void;
    onClose?: () => void;
  } = {},
): HedgeSocket {
  let socket: WebSocket | null = null;
  let closed = false;
  let pingTimer: ReturnType<typeof setInterval> | null = null;
  const backoff = opts.backoffMs ?? 2000;

  const stopPing = () => {
    if (pingTimer != null) {
      clearInterval(pingTimer);
      pingTimer = null;
    }
  };

  const connect = () => {
    if (closed) return;
    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${proto}//${location.host}/ws?pair=${encodeURIComponent(pairId)}`;
    socket = new WebSocket(url);
    socket.onopen = () => {
      opts.onOpen?.();
      // 25 s keep-alive ping. CF Edge can drop idle WSes; the DO has
      // no traffic to push when no commands or position events flow,
      // so without this the connection silently dies after ~10 s and
      // canSend flips false even though PnL via account:tick was the
      // only thing keeping it warm.
      stopPing();
      pingTimer = setInterval(() => {
        if (socket?.readyState === WebSocket.OPEN) {
          try {
            socket.send('{"type":"ping"}');
          } catch {
            /* socket already gone */
          }
        }
      }, 25_000);
    };
    socket.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data) as ServerEvent | { type: "pong" };
        if ((parsed as { type?: string }).type === "pong") return;
        onEvent(parsed as ServerEvent);
      } catch {
        /* ignore malformed frames */
      }
    };
    socket.onclose = () => {
      stopPing();
      opts.onClose?.();
      if (closed) return;
      setTimeout(connect, backoff);
    };
    socket.onerror = () => {
      socket?.close();
    };
  };
  connect();

  return {
    close() {
      closed = true;
      stopPing();
      socket?.close();
    },
    send(msg: unknown) {
      if (!socket || socket.readyState !== WebSocket.OPEN) {
        return false;
      }
      socket.send(JSON.stringify(msg));
      return true;
    },
  };
}
